import { useMemo, useState, type ReactElement } from 'react';
import { ChevronRight } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { EndpointsTable } from '@/components/Inventory/EndpointsTable';
import {
  UnrecordedEndpointReason,
  type GroupedAlert,
  type GroupedAlertEndpoint,
  type GroupedAlertUnrecordedEndpoint,
} from '@kirin/api-client/contracts/alerts';
import { getUserDisplayName } from '@/lib/utils';
import { CLOUD_SESSION_LABEL, CLOUD_SESSION_TITLE } from './cloud-session';
import { cn } from '@/lib/utils';

function endpointLabel(ep: GroupedAlertEndpoint): string {
  return ep.name || ep.machineId;
}

const NO_ENDPOINT_LABEL = 'No endpoint recorded';

/**
 * KIR-5410: the physical machine an entry belongs to. Entries carry one OS
 * user each; on a shared PC they share `hardwareDeviceId`. Without one (flag
 * off, older agents) every entry stays its own endpoint, as before.
 */
function deviceKey(ep: GroupedAlertEndpoint, index: number): string {
  return ep.hardwareDeviceId ? `hw:${ep.hardwareDeviceId}` : `entry:${index}`;
}

/** Entries grouped by machine (first-seen order), so a shared PC's people sit together. */
function groupByDevice(endpoints: GroupedAlertEndpoint[]): {
  deviceCount: number;
  ordered: GroupedAlertEndpoint[];
} {
  const groups = new Map<string, GroupedAlertEndpoint[]>();
  endpoints.forEach((ep, index) => {
    const key = deviceKey(ep, index);
    groups.set(key, [...(groups.get(key) ?? []), ep]);
  });
  return { deviceCount: groups.size, ordered: [...groups.values()].flat() };
}

function isCloudSession(u: GroupedAlertUnrecordedEndpoint): boolean {
  return u.reason === UnrecordedEndpointReason.CloudSession;
}

/**
 * KIR-3035 / KIR-3911: alerts-table "Endpoints" cell. Single endpoint → its
 * name; multiple → "N endpoints". The WHOLE cell is clickable (KIR-3911) and
 * opens the endpoint/user breakdown (Endpoint · User · Alerts, one row per
 * unique (endpoint, user) pair) — including "No endpoint recorded" rows for
 * occurrences that carry no endpoint ("Cloud session" for the Claude Desktop
 * hook's VM, KIR-5139). "Show Endpoints" opens
 * the shared full-details endpoints table (no Inventory link).
 */
export function AlertEndpointsCell({
  alert,
}: {
  alert: GroupedAlert;
}): ReactElement {
  const endpoints = alert.endpoints ?? [];
  const unrecorded = alert.endpointsUnrecorded ?? [];
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // userId → display name, from the alert's own users list (same resolution as
  // the Users column). The endpoint/unrecorded `userId` is the INTERNAL
  // users.id, whereas alert.users[].id is the Descope id once resolved (the
  // internal id then lives on `internalId`) — so index by both to match either.
  const userNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of alert.users) {
      const name = getUserDisplayName(u);
      map.set(u.id, name);
      if (u.internalId) map.set(u.internalId, name);
    }
    return map;
  }, [alert.users]);

  const displayUser = (userId: string | null): string =>
    userId ? (userNameById.get(userId) ?? userId) : 'Unknown';

  const hasAny = endpoints.length > 0 || unrecorded.length > 0;
  if (!hasAny) {
    return <span className="text-muted-foreground">—</span>;
  }

  // Label counts recorded endpoints only — unrecorded rows are "no endpoint"
  // per-user buckets, not endpoints. When there are none, say so — as a cloud
  // session when every bucket is one (KIR-5139).
  // KIR-5410: count machines, not (machine, person) entries — a shared PC
  // used by several people is one endpoint, listed once per person below.
  const { deviceCount, ordered } = groupByDevice(endpoints);
  const first = ordered[0];
  const isSingle = deviceCount === 1 && unrecorded.length === 0;
  const label =
    isSingle && first
      ? endpointLabel(first)
      : deviceCount > 0
        ? `${deviceCount} endpoint${deviceCount === 1 ? '' : 's'}`
        : unrecorded.every(isCloudSession)
          ? CLOUD_SESSION_LABEL
          : NO_ENDPOINT_LABEL;

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Affected endpoints"
            className={cn(
              'flex w-full items-center gap-0.5 min-w-0 text-left underline decoration-dotted underline-offset-2 cursor-pointer hover:text-foreground'
            )}
            onClick={e => e.stopPropagation()}
          >
            <span className="truncate">{label}</span>
            <ChevronRight className="h-3 w-3 flex-shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-96 p-0"
          onClick={e => e.stopPropagation()}
        >
          <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b">
            Affected Endpoints
          </div>
          <div className="max-h-64 overflow-auto">
            <Table className="text-xs">
              <TableHeader className="sticky top-0 bg-muted">
                <TableRow className="!bg-transparent hover:!bg-transparent">
                  <TableHead>Endpoint</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead className="text-right">Instances</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ordered.map(ep => (
                  <TableRow
                    key={`${ep.userId ?? 'unknown'}-${ep.machineId}`}
                    className="border-b"
                  >
                    <TableCell className="truncate max-w-[10rem]">
                      {endpointLabel(ep)}
                    </TableCell>
                    <TableCell className="truncate max-w-[8rem]">
                      {displayUser(ep.userId)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {ep.occurrenceCount}
                    </TableCell>
                  </TableRow>
                ))}
                {unrecorded.map(u => (
                  <TableRow
                    key={`unrecorded-${u.userId ?? 'unknown'}-${u.reason ?? UnrecordedEndpointReason.NotRecorded}`}
                    className="border-b"
                  >
                    <TableCell
                      className="italic text-muted-foreground"
                      title={
                        isCloudSession(u) ? CLOUD_SESSION_TITLE : undefined
                      }
                    >
                      {isCloudSession(u)
                        ? CLOUD_SESSION_LABEL
                        : NO_ENDPOINT_LABEL}
                    </TableCell>
                    <TableCell className="truncate max-w-[8rem]">
                      {displayUser(u.userId)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {u.occurrenceCount}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {endpoints.length > 0 && (
            <div className="border-t p-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-center"
                onClick={() => {
                  setPopoverOpen(false);
                  setDialogOpen(true);
                }}
              >
                Show Endpoints
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="sm:max-w-4xl"
          onClick={e => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle>Endpoint Details</DialogTitle>
          </DialogHeader>
          <EndpointsTable endpoints={endpoints} showAlerts />
        </DialogContent>
      </Dialog>
    </>
  );
}

AlertEndpointsCell.displayName = 'AlertEndpointsCell';
